// src/pages/ProfilePage.tsx
import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Phone,
  Building2,
  Shield,
  Calendar,
  Edit2,
  Save,
  X,
  MapPin,
  Clock,
  CheckCircle2,
  AlertCircle,
  Camera,
  Upload,
  Lock,
  Eye,
  EyeOff,
  Key,
  Settings,
  Activity,
  Bell,
  Trash2,
  Image as ImageIcon,
  Smartphone,
  Copy,
  Check
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import backendApi from "../services/backendApi";
import apiService from "../services/api";
import { getUserAvatar, getUserInitials, storeUserAvatar } from "../utils/userAvatar";
import ImageCropModal from "../components/ImageCropModal";

interface UserProfile {
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  countryCode?: string;
  phoneNumber?: string;
  role?: string;
  joinDate?: string;
  lastLogin?: string;
  avatar?: string;
}

interface PasswordChange {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

type RouteParams = {
  site: string;
};

const ProfilePage: React.FC = () => {
  const { site } = useParams<RouteParams>();
  const navigate = useNavigate();
  const { user: authUser, login } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | 'settings'>('profile');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showSaveNotification, setShowSaveNotification] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [profile, setProfile] = useState<UserProfile>({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    countryCode: '',
    phoneNumber: '',
    role: 'User',
    joinDate: new Date().toISOString().split('T')[0],
    lastLogin: new Date().toISOString(),
    avatar: ''
  });

  const [editedProfile, setEditedProfile] = useState<UserProfile>(profile);

  const [passwordData, setPasswordData] = useState<PasswordChange>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  // Microsoft Authenticator / 2FA State
  const [isMfaEnabled, setIsMfaEnabled] = useState<boolean>(false);
  const [showMfaSetupModal, setShowMfaSetupModal] = useState<boolean>(false);
  const [showMfaDisableModal, setShowMfaDisableModal] = useState<boolean>(false);
  const [mfaSetupData, setMfaSetupData] = useState<any>(null);
  const [mfaSetupLoading, setMfaSetupLoading] = useState<boolean>(false);
  const [mfaConfirmCode, setMfaConfirmCode] = useState<string>('');
  const [mfaConfirmError, setMfaConfirmError] = useState<string>('');
  const [mfaBackupCodes, setMfaBackupCodes] = useState<string[]>([]);
  const [mfaCopied, setMfaCopied] = useState<boolean>(false);
  const [mfaDisablePassword, setMfaDisablePassword] = useState<string>('');
  const [mfaDisableError, setMfaDisableError] = useState<string>('');
  const [mfaDisableLoading, setMfaDisableLoading] = useState<boolean>(false);

  // Check MFA status
  const checkMfaStatus = async () => {
    try {
      const res = await apiService.getMFAStatus();
      if (res && res.success) {
        setIsMfaEnabled(!!res.is_mfa_enabled);
      }
    } catch (e) {
      console.error('Failed to load MFA status:', e);
    }
  };

  useEffect(() => {
    checkMfaStatus();
  }, []);

  const openMfaSetup = async () => {
    setMfaSetupLoading(true);
    setShowMfaSetupModal(true);
    setMfaConfirmError('');
    setMfaConfirmCode('');
    setMfaBackupCodes([]);
    setMfaCopied(false);

    try {
      const res = await apiService.setupMFA();
      if (res && res.success) {
        setMfaSetupData(res);
      } else {
        setMfaConfirmError(res.message || 'Failed to initialize MFA setup.');
      }
    } catch (err: any) {
      setMfaConfirmError(err.message || 'Failed to connect to authentication server.');
    } finally {
      setMfaSetupLoading(false);
    }
  };

  const handleConfirmMfaSetup = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const cleanCode = mfaConfirmCode.trim().replace(/\s+/g, '');
    if (!cleanCode || cleanCode.length !== 6) {
      setMfaConfirmError('Please enter a valid 6-digit verification code.');
      return;
    }

    setMfaSetupLoading(true);
    setMfaConfirmError('');

    try {
      const res = await apiService.confirmMFASetup(cleanCode);
      if (res && res.success) {
        setMfaBackupCodes(res.backup_codes || []);
        setIsMfaEnabled(true);
        setSuccess('Microsoft Authenticator enabled successfully!');
      } else {
        setMfaConfirmError(res.message || 'Invalid code. Please check Microsoft Authenticator.');
      }
    } catch (err: any) {
      setMfaConfirmError(err.message || 'Verification failed. Please try again.');
    } finally {
      setMfaSetupLoading(false);
    }
  };

  const handleDisableMfa = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!mfaDisablePassword) {
      setMfaDisableError('Please enter your password to confirm.');
      return;
    }

    setMfaDisableLoading(true);
    setMfaDisableError('');

    try {
      const res = await apiService.disableMFA(mfaDisablePassword);
      if (res && res.success) {
        setShowMfaDisableModal(false);
        setMfaDisablePassword('');
        setIsMfaEnabled(false);
        setSuccess('Two-Factor Authentication has been disabled.');
      } else {
        setMfaDisableError(res.message || 'Incorrect password.');
      }
    } catch (err: any) {
      setMfaDisableError(err.message || 'Failed to disable MFA.');
    } finally {
      setMfaDisableLoading(false);
    }
  };


  // Load user profile from AuthContext and backend
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);

        // Try to get from backend API first
        const response = await backendApi.getCurrentUser();

        if (response.success && response.user) {
          const user = response.user;
          const profileData: UserProfile = {
            username: user.username || authUser?.username || '',
            firstName: user.first_name || '',
            lastName: user.last_name || '',
            email: user.email || authUser?.email || '',
            countryCode: user.country_code || '',
            phoneNumber: user.phone_number || '',
            role: user.role || authUser?.role || 'User',
            joinDate: user.date_joined || user.created_at || new Date().toISOString().split('T')[0],
            lastLogin: user.last_login || '',
            avatar: user.avatar || user.profile_picture || authUser?.avatar || ''
          };

          setProfile(profileData);
          setEditedProfile(profileData);
          // Set avatarPreview when loading profile
          if (profileData.avatar) {
            setAvatarPreview(profileData.avatar);
          } else {
            // Clear preview if no avatar
            setAvatarPreview(null);
          }
        } else if (authUser) {
          // Fallback to auth context
          const profileData: UserProfile = {
            username: authUser.username || '',
            firstName: authUser.fullName?.split(' ')[0] || '',
            lastName: authUser.fullName?.split(' ').slice(1).join(' ') || '',
            email: authUser.email || '',
            countryCode: '',
            phoneNumber: authUser.phone || '',
            role: authUser.role || 'User',
            joinDate: authUser.joinDate || new Date().toISOString().split('T')[0],
            lastLogin: authUser.lastLogin || '',
            avatar: authUser.avatar || ''
          };

          setProfile(profileData);
          setEditedProfile(profileData);
          // Set avatarPreview when loading profile
          if (profileData.avatar) {
            setAvatarPreview(profileData.avatar);
          } else {
            // Clear preview if no avatar
            setAvatarPreview(null);
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        setError('Failed to load profile data');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [authUser]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditedProfile(profile);
    setAvatarPreview(profile.avatar || null);
    setAvatarFile(null);
    setIsEditing(false);
    setError('');
  };

  const handleAvatarClick = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Reset the file input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        // Open crop modal instead of directly setting preview
        setImageToCrop(result);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleCropComplete = (croppedImage: string) => {
    setAvatarPreview(croppedImage);
    setEditedProfile(prev => ({ ...prev, avatar: croppedImage }));
    setImageToCrop(null);
    setShowCropModal(false);
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    setEditedProfile(prev => ({ ...prev, avatar: '' }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Prepare profile data
      const profileUpdateData: any = {
        first_name: editedProfile.firstName || '',
        last_name: editedProfile.lastName || '',
        email: editedProfile.email || '',
        country_code: editedProfile.countryCode || '',
        phone_number: editedProfile.phoneNumber || ''
      };

      // If avatar is a new file (base64), include it
      if (avatarPreview && avatarPreview.startsWith('data:image')) {
        profileUpdateData.avatar = avatarPreview;
      } else if (avatarPreview && !avatarPreview.startsWith('data:image')) {
        // If it's already a URL, keep it
        profileUpdateData.avatar = avatarPreview;
      }

      // Call backend API
      const response = await backendApi.updateProfile(profileUpdateData);

      if (response.success && response.user) {
        // Get avatar URL from backend response
        const avatarUrl = response.user.avatar || response.user.profile_picture || response.user.profile_image || null;

        // Update local state
        const updatedProfile: UserProfile = {
          ...editedProfile,
          avatar: avatarUrl || ''
        };

        setProfile(updatedProfile);

        // Update avatarPreview with the URL from backend (or keep base64 if URL not available yet)
        if (avatarUrl) {
          setAvatarPreview(avatarUrl);
        } else if (avatarPreview && avatarPreview.startsWith('data:image')) {
          // Keep base64 preview if backend didn't return URL yet
          // This can happen if backend processing is delayed
        }

        setIsEditing(false);
        setAvatarFile(null);
        setSuccess('Profile updated successfully');
        setShowSaveNotification(true);

        // Update AuthContext
        if (login) {
          login({
            ...authUser!,
            fullName: `${updatedProfile.firstName} ${updatedProfile.lastName}`.trim() || authUser?.fullName || '',
            email: updatedProfile.email || authUser?.email || '',
            phone: `${updatedProfile.countryCode || ''} ${updatedProfile.phoneNumber || ''}`.trim() || authUser?.phone || '',
            avatar: avatarUrl || updatedProfile.avatar || authUser?.avatar || ''
          });
        }

        // Store in localStorage for persistence and broadcast to all tabs/components
        localStorage.setItem('userProfile', JSON.stringify(updatedProfile));

        // Store avatar using utility function (handles broadcasting)
        // Pass user identifier (username) to ensure user-specific storage
        const userIdentifier = authUser?.username || updatedProfile.username;
        storeUserAvatar(avatarUrl || updatedProfile.avatar || null, userIdentifier);

        setTimeout(() => {
          setShowSaveNotification(false);
          setSuccess('');
        }, 3000);
      } else {
        setError(response.error || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setError(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      // Call backend API for password change
      const response = await backendApi.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });

      if (response.success) {
        setSuccess('Password changed successfully');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      } else {
        setError(response.error || 'Failed to change password');
      }
    } catch (error: any) {
      console.error('Error changing password:', error);
      setError(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof UserProfile, value: string) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getInitials = () => {
    return getUserInitials({
      first_name: profile.firstName,
      last_name: profile.lastName,
      username: profile.username
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAvatarDisplay = () => {
    // Priority: avatarPreview (current selection/preview) > profile.avatar > localStorage > authUser.avatar
    if (avatarPreview) {
      return avatarPreview;
    }

    if (profile.avatar) {
      return profile.avatar;
    }

    const fromStorage = getUserAvatar({
      avatar: authUser?.avatar,
      profile_picture: authUser?.avatar,
      first_name: profile.firstName,
      last_name: profile.lastName,
      username: profile.username
    });

    return fromStorage;
  };

  if (loading && !profile.username) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-[#F2F8FC]/30 dark:from-[#0B1120] dark:to-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-indigo-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-300 font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page min-h-screen bg-gradient-to-br from-gray-50 to-[#F2F8FC]/30" style={{ fontWeight: 400 }}>

      {/* Notifications */}
      <AnimatePresence>
        {showSaveNotification && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-16 sm:top-20 right-2 sm:right-6 z-[110] bg-white/95 backdrop-blur-xl border border-green-200 rounded-xl sm:rounded-2xl shadow-2xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 max-w-[calc(100vw-1rem)] sm:max-w-sm"
          >
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-full">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-gray-800">Profile Updated</h4>
              <p className="text-xs text-gray-600">{success || 'Your changes have been saved successfully'}</p>
            </div>
          </motion.div>
        )}

        {error && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-16 sm:top-20 right-2 sm:right-6 z-[110] bg-white/95 backdrop-blur-xl border border-red-200 rounded-xl sm:rounded-2xl shadow-2xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 max-w-[calc(100vw-1rem)] sm:max-w-sm"
          >
            <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-full">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-gray-800">Error</h4>
              <p className="text-xs text-gray-600">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="ml-2 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex items-start justify-center min-h-[calc(100vh-50px)] px-2 sm:px-4 pt-16 sm:pt-20 md:pt-24 pb-6 sm:pb-12">
        <div className="max-w-7xl xl:max-w-[90rem] w-full">

          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <div className="flex items-center gap-2 sm:gap-3 mb-4">
              <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-[#0084CF] to-[#0066A3] rounded-xl sm:rounded-2xl shadow-lg flex-shrink-0">
                <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">My Profile</h1>
                <p className="text-xs sm:text-sm text-gray-600">Manage your account information and preferences</p>
              </div>
            </div>
          </motion.div>

          {/* Tabs */}
          <div data-tour="profile-tabs" className="mb-4 sm:mb-6 bg-white rounded-lg sm:rounded-xl p-0.5 sm:p-1 shadow-sm border border-gray-200 inline-flex w-full sm:w-auto overflow-x-auto">
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex-1 sm:flex-none px-3 sm:px-6 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${activeTab === 'profile'
                  ? 'bg-[#0084CF] text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <User className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              Profile
            </button>
            <button
              onClick={() => setActiveTab('password')}
              className={`flex-1 sm:flex-none px-3 sm:px-6 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${activeTab === 'password'
                  ? 'bg-[#0084CF] text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <Lock className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              Password
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex-1 sm:flex-none px-3 sm:px-6 py-2 rounded-lg font-medium text-xs sm:text-sm transition-all whitespace-nowrap ${activeTab === 'settings'
                  ? 'bg-[#0084CF] text-white shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <Settings className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              Settings
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">

            {/* Left Column - Profile Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-4 xl:col-span-3"
            >
              <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-white/60 shadow-xl p-6">
                {/* Avatar with Upload */}
                <div className="flex flex-col items-center mb-4 sm:mb-6">
                  <div className="relative mb-3 sm:mb-4 group">
                    {isEditing ? (
                      <button
                        type="button"
                        className={`w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full ${getAvatarDisplay()
                            ? 'bg-gray-100'
                            : 'bg-gradient-to-br from-[#0084CF] to-[#0066A3]'
                          } flex items-center justify-center shadow-2xl ring-2 sm:ring-4 ring-[#E3F0F9] overflow-hidden transition-all hover:ring-[#8AC4E6] cursor-pointer p-0 border-0 outline-none focus:ring-2 focus:ring-[#0084CF]`}
                        onClick={handleAvatarClick}
                        title="Click to upload photo" aria-label="Click to upload photo"
                      >
                        {getAvatarDisplay() ? (
                          <img
                            src={getAvatarDisplay()!}
                            alt="Profile"
                            className="w-full h-full object-cover pointer-events-none"
                            onError={(e) => {
                              // Fallback to initials if image fails to load
                              e.currentTarget.style.display = 'none';
                              const parent = e.currentTarget.parentElement;
                              if (parent) {
                                const fallback = document.createElement('span');
                                fallback.className = 'text-white text-4xl font-bold pointer-events-none';
                                fallback.textContent = getInitials();
                                parent.appendChild(fallback);
                              }
                            }}
                          />
                        ) : (
                          <span className="text-white text-2xl sm:text-3xl md:text-4xl font-bold pointer-events-none">
                            {getInitials()}
                          </span>
                        )}
                      </button>
                    ) : (
                      <div
                        className={`w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-full ${getAvatarDisplay()
                            ? 'bg-gray-100'
                            : 'bg-gradient-to-br from-[#0084CF] to-[#0066A3]'
                          } flex items-center justify-center shadow-2xl ring-2 sm:ring-4 ring-[#E3F0F9] overflow-hidden`}
                      >
                        {getAvatarDisplay() ? (
                          <img
                            src={getAvatarDisplay()!}
                            alt="Profile"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to initials if image fails to load
                              e.currentTarget.style.display = 'none';
                              const parent = e.currentTarget.parentElement;
                              if (parent) {
                                const fallback = document.createElement('span');
                                fallback.className = 'text-white text-2xl sm:text-3xl md:text-4xl font-bold';
                                fallback.textContent = getInitials();
                                parent.appendChild(fallback);
                              }
                            }}
                          />
                        ) : (
                          <span className="text-white text-2xl sm:text-3xl md:text-4xl font-bold">
                            {getInitials()}
                          </span>
                        )}
                      </div>
                    )}

                    {isEditing && (
                      <>
                        {/* Camera icon overlay to indicate clickability */}
                        <div className="absolute bottom-2 right-2 w-10 h-10 bg-[#0084CF]/90 rounded-full flex items-center justify-center shadow-lg pointer-events-none z-20">
                          <Camera className="w-5 h-5 text-white" />
                        </div>
                        {getAvatarDisplay() && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveAvatar();
                            }}
                            className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:bg-red-600 transition-colors z-30"
                            title="Remove avatar" aria-label="Remove avatar"
                          >
                            <Trash2 className="w-4 h-4 text-white" />
                          </button>
                        )}
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="hidden"
                          id="avatar-upload-input"
                        />
                      </>
                    )}

                    {!isEditing && (
                      <div className="absolute bottom-2 right-2 w-6 h-6 bg-green-400 rounded-full border-4 border-white shadow-lg z-0"></div>
                    )}
                  </div>

                  <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 text-center break-words">
                    {profile.firstName && profile.lastName
                      ? `${profile.firstName} ${profile.lastName}`
                      : profile.username}
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1 break-all">@{profile.username}</p>

                  <div className="flex items-center gap-2 mt-2 sm:mt-3 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#F2F8FC] rounded-full">
                    <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-[#0066A3] flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-semibold text-[#1C4D88] capitalize">
                      {profile.role}
                    </span>
                  </div>

                  {isEditing && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Click avatar to upload new photo
                    </p>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="space-y-2 sm:space-y-3 pt-4 sm:pt-6 border-t border-gray-200">
                  <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-[#F2F8FC] rounded-lg flex-shrink-0">
                      <Building2 className="w-3 h-3 sm:w-4 sm:h-4 text-[#0066A3]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">Current Site</p>
                      <p className="font-semibold text-gray-800 truncate">{site?.toUpperCase()}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-[#F2F8FC] rounded-lg flex-shrink-0">
                      <Phone className="w-3 h-3 sm:w-4 sm:h-4 text-[#0066A3]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">Contact</p>
                      <p className="font-semibold text-gray-800 truncate">{`${profile.countryCode || ''} ${profile.phoneNumber || ''}`.trim() || 'Not Set'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-green-50 rounded-lg flex-shrink-0">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">Joined</p>
                      <p className="font-semibold text-gray-800 truncate">{formatDate(profile.joinDate)}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 bg-orange-50 rounded-lg flex-shrink-0">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">Last Login</p>
                      <p className="font-semibold text-gray-800 truncate">{formatDateTime(profile.lastLogin)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Column - Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-8 xl:col-span-9"
            >
              {activeTab === 'profile' && (
                <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/60 shadow-xl p-4 sm:p-5 lg:p-6">
                  {/* Header with Edit Button */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 sm:mb-4 pb-3 sm:pb-4 border-b border-gray-200">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">Profile Information</h3>
                      <p className="text-xs sm:text-sm text-gray-600 mt-0.5">Update your personal details and contact information</p>
                    </div>

                    {!isEditing ? (
                      <button
                        onClick={handleEdit}
                        className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-[#F2F8FC] hover:bg-[#E3F0F9] text-[#1C4D88] rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 hover:shadow-lg w-full sm:w-auto"
                      >
                        <Edit2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        Edit Profile
                      </button>
                    ) : (
                      <div className="flex gap-2 w-full sm:w-auto">
                        <button
                          onClick={handleCancel}
                          className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 flex-1 sm:flex-none"
                        >
                          <X className="w-3 h-3 sm:w-4 sm:h-4" />
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={loading}
                          className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-[#0084CF] hover:bg-[#0066A3] text-white rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none"
                        >
                          {loading ? (
                            <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <Save className="w-3 h-3 sm:w-4 sm:h-4" />
                          )}
                          Save Changes
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Form Fields */}
                  <div className="space-y-4 sm:space-y-5">
                    {/* Personal Information */}
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-gray-700 mb-2.5 sm:mb-3 flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#0066A3] flex-shrink-0" />
                        <span>Personal Information</span>
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {/* First Name */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-600 mb-1.5 sm:mb-2">
                            First Name
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={editedProfile.firstName}
                              onChange={(e) => handleInputChange('firstName', e.target.value)}
                              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all duration-200 bg-white text-gray-900 placeholder-gray-500 text-sm"
                              style={{ color: '#111827', backgroundColor: '#ffffff' }}
                              placeholder="Enter first name"
                            />
                          ) : (
                            <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm">
                              {profile.firstName || 'Not set'}
                            </div>
                          )}
                        </div>

                        {/* Last Name */}
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2 sm:mb-2.5">
                            Last Name
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={editedProfile.lastName}
                              onChange={(e) => handleInputChange('lastName', e.target.value)}
                              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all duration-200 bg-white text-gray-900 placeholder-gray-500 text-sm"
                              style={{ color: '#111827', backgroundColor: '#ffffff' }}
                              placeholder="Enter last name"
                            />
                          ) : (
                            <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm">
                              {profile.lastName || 'Not set'}
                            </div>
                          )}
                        </div>

                        {/* Username */}
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2 sm:mb-2.5">
                            Username
                          </label>
                          <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-gray-600 flex items-center gap-2 text-sm">
                            <User className="w-4 h-4" />
                            {profile.username}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Username cannot be changed</p>
                        </div>

                        {/* Country Code */}
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2 sm:mb-2.5">
                            Country Code
                          </label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={editedProfile.countryCode}
                              onChange={(e) => handleInputChange('countryCode', e.target.value)}
                              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all duration-200 bg-white text-gray-900 text-sm"
                              style={{ color: '#111827', backgroundColor: '#ffffff' }}
                              placeholder="+91"
                              maxLength={10}
                            />
                          ) : (
                            <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm">
                              {profile.countryCode || 'Not set'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="pt-3 sm:pt-4 border-t border-gray-200">
                      <h4 className="text-xs sm:text-sm font-bold text-gray-700 mb-2.5 sm:mb-3 flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#0066A3] flex-shrink-0" />
                        <span>Contact Information</span>
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {/* Email */}
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2 sm:mb-2.5">
                            Email Address
                          </label>
                          {isEditing ? (
                            <input
                              type="email"
                              value={editedProfile.email}
                              onChange={(e) => handleInputChange('email', e.target.value)}
                              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all duration-200 bg-white text-gray-900 placeholder-gray-500 text-sm"
                              style={{ color: '#111827', backgroundColor: '#ffffff' }}
                              placeholder="Enter email address"
                            />
                          ) : (
                            <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm">
                              {profile.email || 'Not set'}
                            </div>
                          )}
                        </div>

                        {/* Phone */}
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2 sm:mb-2.5">
                            Phone Number
                          </label>
                          {isEditing ? (
                            <input
                              type="tel"
                              value={editedProfile.phoneNumber}
                              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                              className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all duration-200 bg-white text-gray-900 placeholder-gray-500 text-sm"
                              style={{ color: '#111827', backgroundColor: '#ffffff' }}
                              placeholder="Enter phone number"
                              maxLength={20}
                            />
                          ) : (
                            <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm">
                              {profile.phoneNumber || 'Not set'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Account Information */}
                    <div className="pt-3 sm:pt-4 border-t border-gray-200">
                      <h4 className="text-xs sm:text-sm font-bold text-gray-700 mb-2.5 sm:mb-3 flex items-center gap-1.5">
                        <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#0066A3] flex-shrink-0" />
                        <span>Account Information</span>
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {/* Role */}
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2 sm:mb-2.5">
                            Role
                          </label>
                          <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-gray-600 flex items-center gap-2 text-sm">
                            <Shield className="w-4 h-4" />
                            {profile.role}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Contact admin to change role</p>
                        </div>

                        {/* Join Date */}
                        <div>
                          <label className="block text-xs sm:text-sm font-semibold text-gray-600 mb-2 sm:mb-2.5">
                            Member Since
                          </label>
                          <div className="px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-100 border border-gray-200 rounded-lg text-gray-600 flex items-center gap-2 text-sm">
                            <Calendar className="w-4 h-4" />
                            {formatDate(profile.joinDate)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'password' && (
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-white/60 shadow-xl p-6">
                  <div className="mb-6 pb-6 border-b border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <Lock className="w-5 h-5 text-[#0066A3]" />
                      Change Password
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">Update your password to keep your account secure</p>
                  </div>

                  <form onSubmit={handlePasswordChange} className="space-y-4 sm:space-y-6">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 sm:mb-2">
                        Current Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.current ? "text" : "password"}
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                          className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all duration-200 bg-white text-gray-900 placeholder-gray-500 pr-8 sm:pr-10 text-sm sm:text-base"
                          placeholder="Enter current password"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 sm:mb-2">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.new ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                          className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all duration-200 bg-white text-gray-900 placeholder-gray-500 pr-8 sm:pr-10 text-sm sm:text-base"
                          placeholder="Enter new password (min 6 characters)"
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5 sm:mb-2">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.confirm ? "text" : "password"}
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          className="w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg sm:rounded-xl focus:ring-2 focus:ring-[#0084CF] focus:border-[#0084CF] transition-all duration-200 bg-white text-gray-900 placeholder-gray-500 pr-8 sm:pr-10 text-sm sm:text-base"
                          placeholder="Confirm new password"
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-2.5 bg-[#0084CF] hover:bg-[#0066A3] text-white rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                      >
                        {loading ? (
                          <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Key className="w-3 h-3 sm:w-4 sm:h-4" />
                        )}
                        Change Password
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {activeTab === 'settings' && (
                <div className="bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl border border-white/60 shadow-xl p-4 sm:p-6">
                  <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center gap-2">
                      <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-[#0066A3]" />
                      Account Settings
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Manage your account preferences and notifications</p>
                    <p className="mt-3 text-xs sm:text-sm text-[#1C4D88] bg-[#F2F8FC] border border-[#E3F0F9] border-l-[3px] border-l-[#0084CF] rounded px-3 py-2">
                      These preferences can’t be changed yet; the switches show the current defaults.
                    </p>
                  </div>

                  {/* Two-Factor Authentication Card */}
                  <div className="mb-6 p-4 sm:p-5 border border-[#0084CF]/20 rounded-xl sm:rounded-2xl bg-gradient-to-br from-[#F2F8FC] via-white to-[#F2F8FC] shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-start sm:items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0084CF] to-[#0066A3] flex items-center justify-center text-white shadow-md flex-shrink-0">
                          <Smartphone className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="text-base font-bold text-gray-900">Microsoft Authenticator (2FA)</h4>
                            {isMfaEnabled ? (
                              <span className="px-2.5 py-0.5 text-xs font-bold rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1 border border-emerald-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                Active
                              </span>
                            ) : (
                              <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                                Disabled
                              </span>
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1 leading-relaxed">
                            Protect your account with 6-digit TOTP verification codes generated by Microsoft Authenticator or Google Authenticator.
                          </p>
                        </div>
                      </div>

                      <div className="flex-shrink-0 sm:self-center">
                        {isMfaEnabled ? (
                          <button
                            type="button"
                            onClick={() => {
                              setShowMfaDisableModal(true);
                              setMfaDisableError('');
                              setMfaDisablePassword('');
                            }}
                            className="w-full sm:w-auto px-4 py-2 text-xs sm:text-sm font-bold text-red-600 hover:text-red-700 hover:bg-red-50 border border-red-200 rounded-xl transition-all shadow-sm"
                          >
                            Disable 2FA
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={openMfaSetup}
                            className="w-full sm:w-auto px-5 py-2.5 text-xs sm:text-sm font-bold text-white bg-gradient-to-r from-[#0084CF] to-[#0066A3] hover:from-[#0066A3] hover:to-[#1C4D88] rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
                          >
                            <Shield className="w-4 h-4" /> Set Up Authenticator
                          </button>
                        )}
                      </div>
                    </div>
                  </div>


                  <div className="space-y-4 sm:space-y-6">
                    <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl gap-3">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-[#0066A3] flex-shrink-0" />
                        <div className="min-w-0">
                          <h4 className="text-sm sm:text-base font-semibold text-gray-900">Email Notifications</h4>
                          <p className="text-xs sm:text-sm text-gray-600">Receive email updates about your account</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-not-allowed opacity-60 flex-shrink-0">
                        <input type="checkbox" className="sr-only peer" defaultChecked disabled aria-label="Email notifications (not adjustable yet)" />
                        <div className="w-10 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#8AC4E6] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 after:transition-all peer-checked:bg-[#0084CF]"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg sm:rounded-xl gap-3">
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-[#0066A3] flex-shrink-0" />
                        <div className="min-w-0">
                          <h4 className="text-sm sm:text-base font-semibold text-gray-900">Activity Logs</h4>
                          <p className="text-xs sm:text-sm text-gray-600">Track your account activity and login history</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-not-allowed opacity-60 flex-shrink-0">
                        <input type="checkbox" className="sr-only peer" defaultChecked disabled aria-label="Activity logs (not adjustable yet)" />
                        <div className="w-10 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#8AC4E6] rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 after:transition-all peer-checked:bg-[#0084CF]"></div>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Image Crop Modal */}
      <ImageCropModal
        isOpen={showCropModal}
        imageSrc={imageToCrop || ''}
        onClose={() => {
          setShowCropModal(false);
          setImageToCrop(null);
        }}
        onCrop={handleCropComplete}
        aspectRatio={1}
      />

      {/* ============================================================ */}
      {/* MICROSOFT AUTHENTICATOR SETUP MODAL */}
      {/* ============================================================ */}
      {showMfaSetupModal && (
        <div 
          className="fixed inset-0 bg-black/75 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
          style={{ zIndex: 999999 }}
        >
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-gray-100 overflow-hidden flex flex-col my-auto max-h-[92vh]">
            {/* Header - Always visible at top of modal */}
            <div className="bg-gradient-to-r from-[#0084CF] to-[#1C4D88] px-5 sm:px-6 py-4 text-white flex items-center justify-between flex-shrink-0 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Smartphone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-base sm:text-lg leading-tight">Microsoft Authenticator</h3>
                  <p className="text-xs text-blue-100">Two-Factor Authentication Setup</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowMfaSetupModal(false)}
                className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body - Scrollable if screen height is small */}
            <div className="p-5 sm:p-6 overflow-y-auto flex-1">
              {mfaBackupCodes.length > 0 ? (
                /* SUCCESS STATE: Display Backup Codes */
                <div className="space-y-4">
                  <div className="text-center py-2">
                    <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-2">
                      <CheckCircle2 className="w-7 h-7" />
                    </div>
                    <h4 className="text-lg font-black text-gray-900">Authenticator Activated!</h4>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Your account is now secured with Microsoft Authenticator.
                    </p>
                  </div>

                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <p className="text-xs font-bold text-amber-900 mb-1.5 flex items-center gap-1.5">
                      <Shield className="w-4 h-4 text-amber-600" />
                      Save Emergency Backup Codes
                    </p>
                    <p className="text-xs text-amber-700 leading-relaxed mb-3">
                      If you lose access to your phone, these one-time codes can be used to log in. Each code can only be used once.
                    </p>
                    <div className="grid grid-cols-2 gap-2 bg-white p-3 rounded-lg border border-amber-200 font-mono text-xs font-bold text-gray-800 text-center">
                      {mfaBackupCodes.map((c, i) => (
                        <div key={i} className="p-1.5 bg-gray-50 rounded border border-gray-200">
                          {c}
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        navigator.clipboard.writeText(mfaBackupCodes.join('\n'));
                        setMfaCopied(true);
                        setTimeout(() => setMfaCopied(false), 2500);
                      }}
                      className="mt-3 w-full py-2 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 flex items-center justify-center gap-2 transition-colors"
                    >
                      {mfaCopied ? (
                        <>
                          <Check className="w-4 h-4 text-emerald-600" />
                          <span className="text-emerald-600">Copied to Clipboard!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>Copy Backup Codes</span>
                        </>
                      )}
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowMfaSetupModal(false)}
                    className="w-full py-3 bg-[#0084CF] hover:bg-[#0066A3] text-white font-bold rounded-xl transition-all shadow-md"
                  >
                    Done & Return to Profile
                  </button>
                </div>
              ) : (
                /* SETUP STEPS */
                <div className="space-y-4">
                  {/* Step 1 & 2 */}
                  <div className="space-y-2">
                    <div className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#0084CF] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        1
                      </span>
                      <p className="text-xs text-gray-700 leading-relaxed font-semibold">
                        Open <b>Microsoft Authenticator</b> on your phone, tap <b>+</b> and choose <b>Work or school account</b> or <b>Other account</b>.
                      </p>
                    </div>

                    <div className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#0084CF] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        2
                      </span>
                      <p className="text-xs text-gray-700 leading-relaxed font-semibold">
                        Scan the QR code below using your phone's camera inside the app:
                      </p>
                    </div>
                  </div>

                  {/* QR Code Container */}
                  <div className="text-center p-3 bg-gray-50 rounded-xl border border-gray-200">
                    {mfaSetupLoading && !mfaSetupData ? (
                      <div className="py-10 flex flex-col items-center justify-center">
                        <div className="w-8 h-8 border-3 border-[#0084CF] border-t-transparent rounded-full animate-spin mb-2"></div>
                        <p className="text-xs text-gray-500 font-bold">Generating QR Code...</p>
                      </div>
                    ) : mfaSetupData?.qr_code ? (
                      <div className="space-y-2.5">
                        <img
                          src={mfaSetupData.qr_code}
                          alt="Microsoft Authenticator QR Code"
                          className="w-40 h-40 mx-auto bg-white p-2 rounded-xl border-2 border-[#0084CF]/20 shadow-md"
                        />
                        <div>
                          <p className="text-[11px] text-gray-500 mb-1 font-semibold">Can't scan the QR code? Enter this key manually:</p>
                          <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 font-mono text-xs font-bold text-gray-800 max-w-full overflow-x-auto">
                            <span className="break-all">{mfaSetupData.formatted_secret || mfaSetupData.secret}</span>
                            <button
                              type="button"
                              onClick={() => {
                                navigator.clipboard.writeText(mfaSetupData.secret);
                                setMfaCopied(true);
                                setTimeout(() => setMfaCopied(false), 2000);
                              }}
                              className="text-[#0084CF] hover:text-[#0066A3] transition-colors flex-shrink-0"
                              title="Copy key"
                            >
                              {mfaCopied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-red-600 py-4 font-bold">
                        Failed to load QR code. Please check your network connection.
                      </p>
                    )}
                  </div>

                  {/* Step 3: Enter Code Form */}
                  <form onSubmit={handleConfirmMfaSetup} className="space-y-3">
                    <div className="flex items-start gap-2.5">
                      <span className="w-5 h-5 rounded-full bg-[#0084CF] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                        3
                      </span>
                      <p className="text-xs text-gray-700 leading-relaxed font-semibold">
                        Enter the 6-digit verification code from <b>Microsoft Authenticator</b>:
                      </p>
                    </div>

                    <div className="pt-0.5">
                      <input
                        type="text"
                        maxLength={6}
                        placeholder="000000"
                        value={mfaConfirmCode}
                        onChange={(e) => {
                          setMfaConfirmCode(e.target.value.replace(/\D/g, ''));
                          setMfaConfirmError('');
                        }}
                        className="w-full text-center text-2xl font-black tracking-[0.4em] py-2.5 bg-white border-2 border-[#0084CF]/30 rounded-xl focus:outline-none focus:border-[#0084CF] focus:ring-2 focus:ring-[#0084CF]/20 text-gray-900 shadow-inner"
                        disabled={mfaSetupLoading}
                        autoFocus
                      />
                    </div>

                    {mfaConfirmError && (
                      <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs font-bold text-red-600 text-center">
                        {mfaConfirmError}
                      </div>
                    )}

                    <div className="flex items-center gap-3 pt-1">
                      <button
                        type="button"
                        onClick={() => setShowMfaSetupModal(false)}
                        className="w-1/3 py-2.5 border border-gray-300 rounded-xl text-xs font-bold text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={mfaSetupLoading || mfaConfirmCode.length !== 6}
                        className="w-2/3 py-2.5 bg-gradient-to-r from-[#0084CF] to-[#1C4D88] hover:opacity-95 text-white text-xs font-bold rounded-xl transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {mfaSetupLoading ? 'Verifying...' : 'Verify & Enable 2FA'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* DISABLE MFA CONFIRMATION MODAL */}
      {/* ============================================================ */}
      {showMfaDisableModal && (
        <div 
          className="fixed inset-0 bg-black/75 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto"
          style={{ zIndex: 999999 }}
        >
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-100 overflow-hidden my-auto">
            <div className="bg-red-500 px-6 py-4 text-white flex items-center justify-between flex-shrink-0">
              <h3 className="font-bold text-base flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Disable Microsoft Authenticator
              </h3>
              <button
                type="button"
                onClick={() => setShowMfaDisableModal(false)}
                className="text-white hover:opacity-80"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleDisableMfa} className="p-6 space-y-4">
              <p className="text-xs text-gray-600 leading-relaxed font-semibold">
                Disabling Two-Factor Authentication removes Microsoft Authenticator protection from your account. Please enter your password to confirm.
              </p>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Account Password
                </label>
                <input
                  type="password"
                  value={mfaDisablePassword}
                  onChange={(e) => {
                    setMfaDisablePassword(e.target.value);
                    setMfaDisableError('');
                  }}
                  placeholder="Enter your current password"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-200"
                  disabled={mfaDisableLoading}
                  autoFocus
                />
              </div>

              {mfaDisableError && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-lg text-xs font-bold text-red-600 text-center">
                  {mfaDisableError}
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowMfaDisableModal(false)}
                  className="w-1/2 py-2.5 border border-gray-300 rounded-xl text-xs font-bold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={mfaDisableLoading || !mfaDisablePassword}
                  className="w-1/2 py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl transition-all shadow-md disabled:opacity-50"
                >
                  {mfaDisableLoading ? 'Disabling...' : 'Confirm Disable'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ProfilePage;